import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import '@/i18n';

import { GaugeChartColors } from './constants';
import { GaugeChartSkeleton } from './GaugeChartSkeleton';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

import { cn } from '@/utils/tailwind';

import { Card, CardContent } from '../../atoms/Card/Card';

const getColor = (score: number) => {
  if (score < 0) return GaugeChartColors.RED;
  if (score < 50) return GaugeChartColors.ORANGE;
  return GaugeChartColors.GREEN;
};

export type GaugeChartProps = {
  /** Absent while no score is known yet; rendered as an em dash without a needle. */
  score?: number;
  maxScore: number;
  minScore: number;
  description: string;
  isLoading?: boolean;
  className?: string;
};

export const GaugeChart: FC<GaugeChartProps> = ({
  score,
  minScore,
  maxScore,
  description,
  className,
  isLoading,
}) => {
  const { t } = useTranslation('components');

  if (isLoading) {
    return <GaugeChartSkeleton description={description} />;
  }

  return (
    <Card
      className={cn(
        'w-full rounded-[12px] p-4 bg-custom-warm-grey-25 py-[56.5px] px-[24px] flex items-center justify-center',
        className
      )}
    >
      <CardContent className="max-w-[304px]">
        <div className="relative">
          <ResponsiveContainer className="mb-9" width="100%" height={144}>
            <PieChart
              margin={{
                top: 144,
              }}
            >
              <Pie
                data={[{ value: 2 }, { value: 1 }, { value: 1 }]}
                startAngle={180}
                endAngle={0}
                innerRadius={120}
                outerRadius={145}
                dataKey="value"
                stroke="none"
                paddingAngle={4}
                cornerRadius={8}
              >
                <Cell fill={GaugeChartColors.RED} />
                <Cell fill={GaugeChartColors.ORANGE} />
                <Cell fill={GaugeChartColors.GREEN} />
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          {score !== undefined && (
            <div
              className="flex absolute bottom-0 left-1/2 origin-bottom rounded-full"
              style={{
                transform: `translateX(-50%) rotate(${((score - minScore) / (maxScore - minScore)) * 180 - 90}deg)`,
                transformOrigin: 'bottom center',
                height: '155px',
              }}
            >
              <span
                className="bg-white rounded-full w-[10px] h-[40px]"
                style={{
                  outline: `3px solid ${getColor(score)}`,
                }}
              />
            </div>
          )}
          <div className="absolute top-[65px] left-1/2 transform -translate-x-1/2 text-center">
            <p className="text-body-16-regular !text-custom-warm-grey-500">
              {t('gaugeChart.enpsScore')}
            </p>
            <p className="text-body-36-medium">{score ?? '—'}</p>
          </div>

          <p className="text-body-12-regular !text-custom-warm-grey-500 absolute -bottom-[20px] left-[5px]">
            {minScore}
          </p>
          <p className="text-body-12-regular !text-custom-warm-grey-500 absolute -bottom-[20px] right-[5px]">
            {maxScore}
          </p>
        </div>
        <div>
          <p className="text-body-14-regular text-center !text-custom-warm-grey-500">
            {description}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
